import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../../interceptor/axiosInstance';
import { toast } from 'react-toastify';

const EditUserPopup = ({ user, onClose, roles }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    role: '',
    gender: '',
    program: '',
    section: '',
    semester: '',
    department: '',
    designation: '',
    doj: '',
    user_type: '',
    mobile_number: '',
    is_active: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // New loading state
  const [errorMessage, setErrorMessage] = useState('');
  const [programs, setPrograms] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    // Fetch programs, semesters, and sections
    axiosInstance.get(`/programs/details/`)
      .then((response) => {
        const { programs, semesters, designations, departments } = response.data?.data || {};
        setPrograms(programs || []); // Default to empty array if not available
        setSemesters(semesters || []);
        setDesignations(designations || []);
        setDepartments(departments || []);

      })
      .catch((error) => {
        setErrorMessage('Failed to fetch program details.');
      });
  }, []);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true); // Start loading
      setErrorMessage(''); // Reset error message
      try {
        const response = await axiosInstance.get(`accounts/users/${user.id}/`);
        const userData = response.data?.data;

        // Extract role safely


        setFormData((prevFormData) => ({
          ...prevFormData, // Preserve existing values
          ...(userData.first_name && { first_name: userData.first_name }),
          ...(userData.last_name && { last_name: userData.last_name }),
          ...(userData.email && { email: userData.email }),
          ...(userData.username && { username: userData.username }),
          ...(userData.role && { role: userData.role }),
          ...(userData.user_type && { user_type: userData.user_type }),
          ...(userData?.profile?.gender && { gender: userData.profile.gender }),
          ...(userData?.profile?.program && { program: userData.profile.program }),
          ...(userData?.profile?.section && { section: userData.profile.section }),
          ...(userData?.profile?.semester && { semester: userData.profile.semester }),
          ...(userData?.profile?.department && { department: userData.profile.department }),
          ...(userData?.profile?.designation && { designation: userData.profile.designation }),
          ...(userData?.profile?.doj && { doj: userData.profile.doj }),
          ...(userData.mobile_number && { mobile_number: userData.mobile_number }),
          ...(userData.is_active !== undefined && { is_active: userData.is_active }),
        }));
      } catch (error) {
        setErrorMessage('Failed to fetch user data. Please try again.');
      } finally {
        setIsLoading(false); // Stop loading
      }
    };

    fetchUserData();
  }, [user.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage('');

    try {
      const response = await axiosInstance.put(
        `accounts/users/${user.id}/`,
        formData
      );

      toast.success(response.data.message || 'User profile has been successfully updated!');
      onClose();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to save user data. Please try again.';

      toast.error(errorMessage);
      setErrorMessage(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return ReactDOM.createPortal(
      <div className="edit-user-modal-overlay">
        <div className="edit-user-modal-container">
          <div className="edit-user-modal-loading">
            <div className="edit-user-modal-spinner"></div>
            <p>Loading user data...</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="edit-user-modal-overlay">
      <div className="edit-user-modal-container">
        <div className="edit-user-modal-header">
          <h2>Edit User Profile</h2>
          <span className="edit-user-modal-close" onClick={onClose}>
            &times;
          </span>
        </div>

        <div className="edit-user-modal-content">
          {errorMessage && (
            <div className="edit-user-modal-error">
              <span className="edit-user-modal-error-icon">⚠</span>
              {errorMessage}
            </div>
          )}

          <form className="edit-user-modal-form">
            {/* Basic Information */}
            <div className="edit-user-modal-section">
              <h3 className="edit-user-modal-section-title">Basic Information</h3>
              
              <div className="edit-user-modal-row">
                <div className="edit-user-modal-field">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                  />
                </div>
                <div className="edit-user-modal-field">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className="edit-user-modal-row">
                <div className="edit-user-modal-field">
                  <label>Email ID</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="user@example.com"
                  />
                </div>
                <div className="edit-user-modal-field">
                  <label>Mobile Number</label>
                  <input
                    type="tel"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number"
                  />
                </div>
              </div>

              <div className="edit-user-modal-row">
                <div className="edit-user-modal-field">
                  <label>Registration Number</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    disabled={formData.first_time}
                    placeholder="Enter registration number"
                  />
                </div>
                <div className="edit-user-modal-field">
                  <label>Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Role</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.role_with_suborg}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="edit-user-modal-row">
                <div className="edit-user-modal-field">
                  <label>User Type</label>
                  <select
                    name="user_type"
                    value={formData.user_type}
                    onChange={handleInputChange}
                  >
                    <option value="">Select User Type</option>
                    <option value="student">Student</option>
                    <option value="faculty">Faculty</option>
                    <option value="prospective_faculty">Prospective Faculty</option>
                  </select>
                </div>
                <div className="edit-user-modal-field">
                  <label>Is Active</label>
                  <select
                    name="is_active"
                    value={formData.is_active === true ? 'true' : 'false'}
                    onChange={(e) => {
                      const isTrue = e.target.value === 'true';
                      setFormData((prevState) => ({
                        ...prevState,
                        is_active: isTrue,
                      }));
                    }}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Student-specific fields */}
            {formData.user_type === 'student' && (
              <div className="edit-user-modal-section">
                <h3 className="edit-user-modal-section-title">Student Details</h3>
                
                <div className="edit-user-modal-row">
                  <div className="edit-user-modal-field">
                    <label>Program</label>
                    <select
                      name="program"
                      value={formData.program}
                      onChange={handleInputChange}
                    >
                      <option value={formData.program} disabled>
                        {formData.program}
                      </option>
                      {programs.map((program) => (
                        <option key={program.id} value={program.name}>
                          {program.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-user-modal-field">
                    <label>Semester</label>
                    <select
                      name="semester"
                      value={formData.semester}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Semester</option>
                      {semesters
                        .filter((semester) => semester.name.toLowerCase() !== 'all')
                        .map((semester) => (
                          <option key={semester.id} value={semester.name}>
                            {semester.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="edit-user-modal-field">
                    <label>Section</label>
                    <select
                      name="section"
                      value={formData.section}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Section</option>
                      {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map((sec) => (
                        <option key={sec} value={sec}>
                          {sec}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Faculty-specific fields */}
            {['faculty', 'prospective_faculty'].includes(formData.user_type) && (
              <div className="edit-user-modal-section">
                <h3 className="edit-user-modal-section-title">Faculty Details</h3>
                
                <div className="edit-user-modal-row">
                  <div className="edit-user-modal-field">
                    <label>Designation</label>
                    <select
                      name="designation"
                      value={formData.designation}
                      onChange={handleInputChange}
                    >
                      <option value={formData.designation} disabled>
                        {formData.designation}
                      </option>
                      {designations.map((designation) => (
                        <option key={designation.id} value={designation.name}>
                          {designation.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-user-modal-field">
                    <label>Department</label>
                    <select
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                    >
                      <option value={formData.department} disabled>
                        {formData.department}
                      </option>
                      {departments.map((department) => (
                        <option key={department.id} value={department.name}>
                          {department.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-user-modal-field">
                    <label>Date of Joining</label>
                    <input
                      type="date"
                      name="doj"
                      value={formData.doj}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="edit-user-modal-actions">
              <button
                type="button"
                className="edit-user-modal-cancel"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="edit-user-modal-save"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <span className="edit-user-modal-save-spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EditUserPopup;
